import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import React from "react";
import { BrowserRouter as Router, Routes, Route, Link } from "react-router-dom";
import Sidebar from "./components/Sidebar";
import MarkdownView from "./components/MarkdownView";
import About from "./components/About";
import DownloadsBook from "./components/DownloadsBook";
import Contributors from "./components/Contributors";
import Certification from "./components/Certification";
import WebVulnerabilityScanner from "./components/WebVulnerabilityScanner";
import Home from "./components/Home";
import LearnGrid from "./components/LearnGrid";
import Playbooks from "./components/Playbooks";
import Labs from "./components/Labs";
import Services from "./components/Services";
import Consulting from "./components/Consulting";
import articles from "./components/articlesData";
import logo from "./assets/images/LSO.png";
import "./App.css";
import "./tokens.css";

function MainAppContent() {
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredArticles, setFilteredArticles] = useState(articles);
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("");

  const navigate = useNavigate();

  useEffect(() => {
    let filtered = articles;

    if (selectedCategory && selectedCategory !== "All Categories") {
      filtered = filtered.filter(
        (article) => article.category === selectedCategory
      );
    }

    if (searchTerm.trim() !== "") {
      filtered = filtered.filter(
        (article) =>
          article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (article.content &&
            article.content.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    setFilteredArticles(filtered);
  }, [searchTerm, selectedCategory]);

  const handleArticleClick = (article) => {
    navigate(article.path);
  };

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 300) {
        setShowScrollToTop(true);
      } else {
        setShowScrollToTop(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!event.target.closest(".custom-dropdown")) {
        setDropdownOpen(false);
      }
    };

    if (dropdownOpen) {
      document.addEventListener("click", handleOutsideClick);
    } else {
      document.removeEventListener("click", handleOutsideClick);
    }

    return () => {
      document.removeEventListener("click", handleOutsideClick);
    };
  }, [dropdownOpen]);

  const location = useLocation();
  const isMarketingRoute = [
    "/",
    "/wvs",
    "/playbooks",
    "/labs",
    "/about",
    "/services",
    "/consulting",
    "/downloads",
    "/community",
  ].includes(location.pathname);

  return (
    <div className={`main-layout${isMarketingRoute ? " marketing" : ""}`}>
      {!isMarketingRoute && (
        <div className="sidebar-container">
          <div className="search-box-container">
            <input
              type="text"
              placeholder="Search articles..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-box"
              id="search-articles"
              name="search-articles"
              aria-label="Search articles"
            />
          </div>
          <div className="dropdown-container">
            <div
              className={`custom-dropdown ${dropdownOpen ? "open" : ""}`}
              onClick={(e) => {
                e.stopPropagation();
                setDropdownOpen(!dropdownOpen);
              }}
            >
              <span>{selectedCategory || "All Categories"}</span>
            </div>
            <div
              className={`custom-dropdown-menu ${dropdownOpen ? "open" : ""}`}
            >
              {[
                "All Categories",
                "PROGRAMMING",
                "CYBER SECURITY",
                "NETWORKING",
                "AWS",
              ].map((category) => (
                <div
                  key={category}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedCategory(
                      category === "All Categories" ? "" : category
                    );
                    setDropdownOpen(false);
                  }}
                  className="custom-dropdown-item"
                >
                  {category}
                </div>
              ))}
            </div>
          </div>
          <Sidebar
            articles={filteredArticles}
            onArticleClick={handleArticleClick}
          />
        </div>
      )}
      <div className={isMarketingRoute ? "marketing-view" : "article-view"}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/learn" element={<LearnGrid />} />
          <Route path="/downloads" element={<DownloadsBook />} />
          <Route path="/playbooks" element={<Playbooks />} />
          <Route path="/labs" element={<Labs />} />
          <Route path="/community" element={<Contributors />} />
          <Route path="/certification" element={<Certification />} />
          <Route
            path="/web-vulnerability-scanner"
            element={<WebVulnerabilityScanner />}
          />
          <Route path="/wvs" element={<WebVulnerabilityScanner />} />
          <Route path="/services" element={<Services />} />
          <Route path="/consulting" element={<Consulting />} />
          {filteredArticles.map((article) => (
            <Route
              key={article.id}
              path={article.path}
              element={<MarkdownView filePath={article.file} />}
            />
          ))}
        </Routes>
      </div>
      {showScrollToTop && (
        <button className="scroll-to-top" onClick={scrollToTop}>
          Back to Top
        </button>
      )}
    </div>
  );
}

function App() {
  return (
    <Router>
      <div className="app-container">
        {/* Header */}
        <header className="header">
          <div className="header-content">
            <div className="header-row">
              <Link to="/" className="logo-link">
                <img src={logo} alt="LinuxSecOps" className="logo" />
              </Link>
              <nav className="nav-links">
                <Link to="/">Home</Link>
                <Link to="/learn">Notes</Link>
                <Link to="/labs">Labs</Link>
                <Link to="/wvs">PentestScan</Link>
                <Link to="/services">Services</Link>
                <Link to="/consulting" className="nav-consulting-btn">Consulting</Link>
                <Link to="/about">About</Link>
              </nav>
              <a
                href="https://pentestscan.app"
                target="_blank"
                rel="noopener noreferrer"
                className="header-cta"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                </svg>
                WVS Scanner
              </a>
              <div className="header-social">
                <a
                  href="https://www.linkedin.com/in/sandro-radinkovic-18a8a35/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-icon-link"
                  aria-label="LinkedIn"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </a>
                <a
                  href="https://github.com/xilpanda"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-icon-link"
                  aria-label="GitHub"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </header>

        <MainAppContent />

        {/* Footer */}
        <footer className="footer">
          <div className="footer-inner">
            <div className="footer-brand">LinuxSecOps</div>
            <div className="footer-links">
              <Link to="/wvs" className="footer-link">
                PentestScan
              </Link>
              <Link to="/services" className="footer-link">
                Services
              </Link>
              <Link to="/consulting" className="footer-link">
                Consulting
              </Link>
              <Link to="/playbooks" className="footer-link">
                Playbooks
              </Link>
              <Link to="/downloads" className="footer-link">
                Downloads
              </Link>
              <Link to="/community" className="footer-link">
                Community
              </Link>
            </div>
            <div className="footer-social">
              <a
                href="https://www.linkedin.com/in/sandro-radinkovic-18a8a35/"
                target="_blank"
                rel="noopener noreferrer"
                className="footer-social-link"
                aria-label="LinkedIn"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </a>
              <a
                href="https://github.com/xilpanda"
                target="_blank"
                rel="noopener noreferrer"
                className="footer-social-link"
                aria-label="GitHub"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                </svg>
              </a>
            </div>
          </div>
        </footer>
      </div>
    </Router>
  );
}

export default App;
